import { UnresolvedCommitDescriptor } from 'custom-types/UnresolvedCommitDescriptor';
import { createPath, parsePath, type Path } from 'ts/base/LocationUtils';
import { Links } from 'ts/commons/links/Links';
import { StringUtils } from 'ts/commons/StringUtils';
import { ETeamscalePerspective } from 'typedefs/ETeamscalePerspective';

/** Logic for adjusting (legacy) URLs. */
export default class Redirects {
	private static readonly PREFIX_REDIRECTS: Array<[RegExp, string]> = [
		[
			/\/issues\.html#?\/([^/]+)\/\?metric=([^?]*)/,
			`/${ETeamscalePerspective.ACTIVITY.simpleName}.html#issues/$1/?action=list&metric=$2`
		],
		[
			/\/issues\.html#?\/([^/]+)\/([^?]*)\??/,
			`/${ETeamscalePerspective.ACTIVITY.simpleName}.html#issues/$1/?action=view&id=$2&`
		],
		[/\/issues\.html/, `/${ETeamscalePerspective.ACTIVITY.simpleName}.html#issues`],
		[/\/tasks\.html#?/, `/${ETeamscalePerspective.QUALITY_CONTROL.simpleName}.html#tasks`],
		[/\/reports\.html/, `/${ETeamscalePerspective.QUALITY_CONTROL.simpleName}.html#reports`],
		[/\/findings\.html#baselines/, `/${ETeamscalePerspective.QUALITY_CONTROL.simpleName}.html#baselines`],
		[/\/tests\.html/, `/${ETeamscalePerspective.TEST_GAPS.simpleName}.html`],
		[/\/metrics\.html#test-executions/, `/${ETeamscalePerspective.METRICS.simpleName}.html#tests`],
		[/\/dashboard\.html#kiosk(.*)/, `/${ETeamscalePerspective.DASHBOARD.simpleName}.html#$1&kioskViewMode=true`]
	];

	/**
	 * Creates a redirect url based on the given window location. Returns <code>null</code> if no redirect is necessary.
	 * Non-null results of this method should be applied using {@link document#location#replace} Example:
	 *
	 * - Input with <code>window.location.href === 'http://foo.com:8080/teamscale/issues.html'</code>
	 * - Result: <code>'http://foo.com:8080/teamscale/activity/issues?foo=bar'</code>
	 */
	public static createRedirectOrNull(locationHref: string): string | null {
		const rewriteCompareLink = Redirects.rewriteCompareLink(locationHref);
		if (rewriteCompareLink !== null) {
			return rewriteCompareLink;
		}
		let intermediateHref = locationHref;
		// Apply rather old perspective and view renames
		for (const patternAndReplacement of Redirects.PREFIX_REDIRECTS) {
			if (intermediateHref.match(patternAndReplacement[0])) {
				intermediateHref = locationHref.replace(patternAndReplacement[0], patternAndReplacement[1]);
				break;
			}
		}
		// Apply the html -> browser router redirects (TS-33333)
		intermediateHref = Redirects.rewriteHtmlToBrowserUrls(intermediateHref);
		intermediateHref = Redirects.rewriteActionRemoval(intermediateHref);
		intermediateHref = Redirects.rewriteTaskIdUrl(intermediateHref);
		intermediateHref = Redirects.rewriteTgaBaselineRename(intermediateHref);
		intermediateHref = Redirects.renameIndexToSlide(intermediateHref);
		intermediateHref = Redirects.rewriteArrayParameters(intermediateHref);
		intermediateHref = Redirects.rewriteBaselineEncoding(intermediateHref);
		intermediateHref = Redirects.rewriteNumericStringParameter(intermediateHref);
		intermediateHref = Redirects.rewriteCategoryParameter(intermediateHref);
		intermediateHref = Redirects.rewriteUniformPathEncoding(intermediateHref);
		intermediateHref = Redirects.rewriteCommitFilterParameters(intermediateHref);
		intermediateHref = Redirects.rewriteArchitectureEditorUrls(intermediateHref);
		// Also apply the redirects in the target query param of login links
		intermediateHref = Redirects.rewriteLoginTarget(intermediateHref);
		// Only return the URL if any redirect needs to happen
		return intermediateHref === locationHref ? null : intermediateHref;
	}

	/**
	 * Rewrites URLs to the new format (see TS-33333). For example `/dashboard.html#//?id=foo` becomes
	 * `/dashboard/show?id=foo`.
	 */
	private static rewriteHtmlToBrowserUrls(urlPathAndHash: string): string {
		if (urlPathAndHash.match(/\/([a-z-_]*)\.html/)) {
			const path = parsePath(urlPathAndHash);
			const newPath = Redirects.relativeLocationToArtificialPath(path);

			/** Adds the default view name if it is not given in the url i.e. /dashboard become /dashboard/show. */
			function addDefaultSubview(perspective: string, view: string) {
				newPath.pathname = newPath.pathname.replaceAll(
					new RegExp(`(^|https?://[^/]+)/${perspective}(//|/?$)`, 'g'),
					`$1/${perspective}/${view}/`
				);
			}

			addDefaultSubview('dashboard', 'show');
			addDefaultSubview('activity', 'commits');
			addDefaultSubview('findings', 'list');
			addDefaultSubview('metrics', 'code');
			addDefaultSubview('requirements-tracing', 'overview');
			addDefaultSubview('compare', 'code');
			addDefaultSubview('testgaps', 'code');
			addDefaultSubview('qualitycontrol', 'reports');
			addDefaultSubview('architecture', 'architecture');
			addDefaultSubview('delta', 'input');
			addDefaultSubview('project', 'project');
			addDefaultSubview('system', 'execution');
			newPath.pathname = StringUtils.stripSuffix(newPath.pathname, '/');
			newPath.pathname = StringUtils.stripSuffix(newPath.pathname, '/');
			return createPath(newPath);
		}

		return urlPathAndHash;
	}

	/** Applies the removal of the action parameter in various views and renames to the new view names. */
	private static rewriteActionRemoval(urlPathAndQuery: string): string {
		let newPath = urlPathAndQuery;

		/**
		 * Renames the view and removed the action query param if the perspective/view/action combination is found i.e.,
		 * /activity/issues?action=view becomes /activity/issue.
		 */
		function replaceAction(perspective: string, view: string, action: string, newViewName: string) {
			newPath = newPath.replaceAll(
				new RegExp(`(^|https?://[^/]+)/${perspective}/${view}(.*)action=${action}&?(.*)`, 'g'),
				`$1/${perspective}/${newViewName}$2$3`
			);
		}

		replaceAction('activity', 'issues', 'view', 'issue');
		replaceAction('admin', 'external-accounts', 'roles', 'external-account-roles');
		replaceAction('admin', 'external-storage-backends', 'roles', 'external-storage-roles');
		replaceAction('project', 'profilers', 'show', 'profiler-config');
		replaceAction('project', 'profilers', 'edit', 'profiler-edit');
		replaceAction('project', 'profilers', 'copy', 'profiler-copy');
		replaceAction('project', 'profilers', 'edit-roles', 'profiler-roles');
		replaceAction('qualitycontrol', 'reports', 'view', 'report-view');
		replaceAction('qualitycontrol', 'reports', 'edit', 'report-edit');
		replaceAction('qualitycontrol', 'reports', 'print', 'report-print');
		replaceAction('qualitycontrol', 'reports', 'present', 'report-present');
		replaceAction('qualitycontrol', 'reports', 'report-roles', 'report-roles');
		replaceAction('qualitycontrol', 'tasks', 'new', 'task-edit');
		replaceAction('qualitycontrol', 'tasks', 'details', 'task-view');
		replaceAction('qualitycontrol', 'tasks', 'edit', 'task-edit');
		replaceAction('qualitycontrol', 'retrospectives', 'edit', 'retrospective-edit');
		replaceAction('qualitycontrol', 'retrospectives', 'retrospective-roles', 'retrospective-roles');
		newPath = StringUtils.stripSuffix(newPath, '?');
		newPath = StringUtils.stripSuffix(newPath, '&');
		return newPath;
	}

	private static rewriteTgaBaselineRename(urlPathAndQuery: string): string {
		return urlPathAndQuery.replaceAll(
			/(.*\/(?:testgaps|activity\/method-history)\/.*[&?])baseline(=.*)/g,
			'$1tga-baseline$2'
		);
	}

	private static renameIndexToSlide(urlPathAndQuery: string): string {
		return urlPathAndQuery.replaceAll(/(.*\/qualitycontrol\/report-(?:view|edit).*[?&])index(=.*)/g, '$1slide$2');
	}

	/** Converts a path from the old html based format to the new URL layout. */
	private static relativeLocationToArtificialPath(browserPath: Path): Path {
		let { pathname, hash } = browserPath;
		pathname = StringUtils.stripSuffix(pathname, '/');
		pathname = StringUtils.stripSuffix(pathname, '.html');
		pathname += '/';
		if (!hash) {
			return {
				pathname,
				search: '',
				hash: ''
			};
		}
		const hashPath = parsePath(hash.substring(1));
		const hashPathname = hashPath.pathname;
		if (hashPathname) {
			pathname += hashPathname;
		}
		return {
			pathname,
			search: hashPath.search,
			hash: hashPath.hash
		};
	}

	/**
	 * Rewrites the compare link to the new format from Teamscale versions v8.6.17 (new format was introduced in
	 * TS-33428).
	 */
	private static rewriteCompareLink(urlPathAndHash: string) {
		if (
			!(
				urlPathAndHash.includes('/compare.html#/') &&
				urlPathAndHash.includes('#@#') &&
				urlPathAndHash.includes('#&#')
			)
		) {
			return null;
		}
		const [urlPrefix, hash] = urlPathAndHash.split('/compare.html#/');
		const parts = decodeURIComponent(hash!.replace(/\+/g, ' ')).split(/#&#/, 4);

		// Full path might contain line information for scrolling
		const leftFullPath = parts[0]!;
		const rightFullPath = parts[1]!;

		const isInconsistentClone = parts.length > 3 && parts[3] === 'isInconsistentClone';
		let leftStartLine, leftEndLine, rightStartLine, rightEndLine;
		if (parts.length > 2) {
			[, leftStartLine, leftEndLine, rightStartLine, rightEndLine] = parts[2]!.match(/(\d+)-(\d+):(\d+)-(\d+)/)!;
		}

		//The first capturing group (.+?) matches the project name or alias. The second capturing group (.+?) after the
		// slash matches the file's uniform path. It is then followed by the last optional capturing group matching the
		// '#@#' followed by the timestamp information.
		const matcher = /^(.+?)\/(.+?)(#@#.+)?$/;
		const leftProjectAndPathMatch = leftFullPath.match(matcher)!;
		const leftProject = leftProjectAndPathMatch[1]!;
		const leftUniformPath = leftProjectAndPathMatch[2]!;
		const leftAdditionInfo = Redirects.parseAdditionalPathInfo(leftProjectAndPathMatch[3]);
		const rightProjectAndPathMatch = rightFullPath.match(matcher)!;
		const rightProject = rightProjectAndPathMatch[1]!;
		const rightUniformPath = rightProjectAndPathMatch[2]!;
		const rightAdditionInfo = Redirects.parseAdditionalPathInfo(rightProjectAndPathMatch[3]);
		return (
			urlPrefix +
			'/' +
			Links.comparePerspective(
				{
					project: leftProject,
					uniformPath: leftUniformPath,
					commit: leftAdditionInfo.commit!,
					startLine: leftStartLine ? Number(leftStartLine) : leftAdditionInfo.initialLine,
					endLine: Number(leftEndLine)
				},
				{
					project: rightProject,
					uniformPath: rightUniformPath,
					commit: rightAdditionInfo.commit!,
					startLine: rightStartLine ? Number(rightStartLine) : rightAdditionInfo.initialLine,
					endLine: Number(rightEndLine)
				},
				{ isInconsistentClone }
			)
		);
	}

	/**
	 * If possible, parses the branch, timestamp and initial line number from the given commit string. If the string is
	 * null, undefined or doesn't match any of the possible commit info patterns, nothing happens.
	 */
	private static parseAdditionalPathInfo(commitInfo: string | undefined): {
		commit?: UnresolvedCommitDescriptor;
		initialLine?: number;
	} {
		if (commitInfo == null) {
			return {};
		}
		const separator = '(#@#)';
		const timestampPattern = '(?<timestamp>(\\d{9,13}|HEAD)(p\\d+)?)';
		const branchAndTimestampPattern = '(?<branch>[^:]*)(?<colon>:?)' + timestampPattern;
		const lineNumberPattern = '(:)(\\d+)';

		// Check for the three cases:
		// (1) (opt_branch :) timestamp : line number
		const branchWithTimestampAndLine = commitInfo.match(
			new RegExp(separator + branchAndTimestampPattern + lineNumberPattern + '$')
		);
		if (branchWithTimestampAndLine != null) {
			return {
				commit: this.getCommit(branchWithTimestampAndLine),
				initialLine: parseInt(branchWithTimestampAndLine[8]!, 10)
			};
		}

		// (2) (opt_branch :) timestamp
		const branchWithTimestamp = commitInfo.match(new RegExp(separator + branchAndTimestampPattern + '$'));
		if (branchWithTimestamp != null) {
			return {
				commit: this.getCommit(branchWithTimestamp)
			};
		}

		// (3) (opt_branch :) line number
		const branchWithLine = commitInfo.match(new RegExp(separator + '((?<branch>[^:]+):)?(\\d+)$'));
		if (branchWithLine != null) {
			return {
				commit: this.getCommit(branchWithLine),
				initialLine: parseInt(branchWithLine[4]!, 10)
			};
		}

		return {};
	}

	/** Extracts the commit from the matched regex array. */
	private static getCommit(match: RegExpMatchArray) {
		const branchAndTimestamp =
			(match.groups!.branch ?? '') + (match.groups!.colon ?? '') + (match.groups!.timestamp ?? '');
		return UnresolvedCommitDescriptor.fromString(branchAndTimestamp) ?? undefined;
	}

	/** Rewrites the "target" search parameter according to #rewriteHtmlToBrowserUrls (see TS-38894). */
	private static rewriteLoginTarget(urlPathAndHash: string): string {
		const { pathname = '', search = '', hash = '' } = parsePath(urlPathAndHash);
		const urlSearchParams = new URLSearchParams(search);
		const target = urlSearchParams.get('target');
		if (pathname.includes('/login') && target != null) {
			const newTarget = Redirects.createRedirectOrNull('/' + target);
			if (newTarget != null) {
				urlSearchParams.set('target', StringUtils.stripPrefix(newTarget, '/'));
				return createPath({ pathname, search: '?' + urlSearchParams.toString(), hash });
			}
		}
		return urlPathAndHash;
	}

	private static readonly KNOWN_ARRAY_PARAMS = new Set([
		'partition',
		'partitions',
		'tags',
		'tools',
		'languages',
		'users',
		'groups',
		'filter',
		'assessment-filters',
		'included-paths',
		'excluded-paths',
		'guideline-rules',
		'guidelines',
		'crossAnnotationProjects',
		'author',
		'assignee',
		'statuses'
	]);

	private static rewriteArrayParameters(urlPathAndQuery: string): string {
		// Parse the URL to get search parameters (for the main part, not hash)
		const { pathname, hash, search = '' } = parsePath(urlPathAndQuery);
		const urlSearchParams = new URLSearchParams(search);

		// Get all parameter names
		const paramNames = new Set<string>();
		urlSearchParams.forEach((_, key) => {
			paramNames.add(key);

			// Also check for parameters with -empty suffix
			if (key.endsWith('-empty')) {
				paramNames.add(key.substring(0, key.length - 6));
			}
		});

		let modified = false;

		// Process each parameter
		for (const paramName of paramNames) {
			// Get all values for this parameter
			const values = urlSearchParams.getAll(paramName);

			// Check for multiple occurrences
			if (
				values.length > 1 ||
				(Redirects.KNOWN_ARRAY_PARAMS.has(paramName) && values.length === 1 && !values[0]!.startsWith('['))
			) {
				// Create the encoded array string
				let encodedArray = '[';
				encodedArray += values.map(value => `"${value}"`).join(',');
				encodedArray += ']';

				// Update the parameter
				urlSearchParams.delete(paramName);
				urlSearchParams.set(paramName, encodedArray);
				modified = true;
			}

			// Check for empty array case
			const emptyParam = `${paramName}-empty`;
			if (urlSearchParams.get(emptyParam) === 'true') {
				urlSearchParams.delete(emptyParam);
				urlSearchParams.set(paramName, '[]');
				modified = true;
			}
		}

		if (!modified) {
			return urlPathAndQuery;
		}

		return createPath({
			pathname,
			search: urlSearchParams.toString() ? `?${urlSearchParams.toString()}` : '',
			hash
		});
	}

	private static rewriteBaselineEncoding(urlPathAndQuery: string) {
		return urlPathAndQuery.replaceAll(
			/(TIMESTAMP|BASELINE|TIMESPAN|REVISION|GIT_TAG|SYSTEM_VERSION)%252B/g,
			'$1%2B'
		);
	}

	/** See TS-32635 */
	private static rewriteTaskIdUrl(urlPathAndQuery: string) {
		return urlPathAndQuery.replaceAll(/(.*\/qualitycontrol\/)tasks(\/.*\?id=\d+)/g, '$1task-view$2');
	}

	private static rewriteNumericStringParameter(urlPathAndQuery: string) {
		return urlPathAndQuery.replaceAll(/(.*\/qualitycontrol\/(?:report-|retrospective).*[&?]id=)(\d+)/g, '$1"$2"');
	}

	private static rewriteCategoryParameter(urlPathAndQuery: string) {
		return urlPathAndQuery.replaceAll(/(admin\/options\?)action(=.*)/g, '$1category$2');
	}

	/**
	 * Rewrites commit filter URL parameters from old to new format:
	 *
	 * - Commit-message: unwraps array if single element ["foo"] -> "foo"
	 * - Exclude-other-branches: removes string quotes "true" -> true
	 * - Commit-types: inverts the selection - old format listed selected types, new format uses boolean params for
	 *   deselected types
	 */
	private static rewriteCommitFilterParameters(urlPathAndQuery: string): string {
		if (!urlPathAndQuery.includes('/activity/commits/')) {
			return urlPathAndQuery;
		}

		const { pathname, hash, search = '' } = parsePath(urlPathAndQuery);
		const urlSearchParams = new URLSearchParams(search);
		let modified = false;

		// Handle commit-message: unwrap array if single element
		const commitMessage = urlSearchParams.get('commit-message');
		if (commitMessage && commitMessage.startsWith('[') && commitMessage.endsWith(']')) {
			try {
				const parsed = JSON.parse(commitMessage);
				if (Array.isArray(parsed) && parsed.length === 1) {
					urlSearchParams.set('commit-message', JSON.stringify(parsed[0]));
					modified = true;
				}
			} catch {
				// If parsing fails, leave it as is
			}
		}

		// Handle exclude-other-branches: remove string quotes
		const excludeOtherBranches = urlSearchParams.get('exclude-other-branches');
		if (excludeOtherBranches === '"true"' || excludeOtherBranches === '"false"') {
			urlSearchParams.set('exclude-other-branches', excludeOtherBranches.slice(1, -1));
			modified = true;
		}

		// Handle commit-types: invert the selection
		// Old format listed the selected types, new format uses boolean params for deselected types
		const commitTypesParam = urlSearchParams.get('commit-types');
		if (commitTypesParam) {
			try {
				const selectedTypes = JSON.parse(commitTypesParam) as string[];
				const allTypes = [
					'CODE_COMMIT',
					'ARCHITECTURE_CHANGE',
					'CODE_REVIEW',
					'EXTERNAL_ANALYSIS',
					'BLACKLIST_COMMIT',
					'SOURCE_LIBRARY_CHANGE'
				];

				// Remove the old parameter
				urlSearchParams.delete('commit-types');

				// Set each deselected type to false
				for (const type of allTypes) {
					if (!selectedTypes.includes(type)) {
						urlSearchParams.set(type, 'false');
					}
				}

				modified = true;
			} catch {
				// If parsing fails, leave it as is
			}
		}

		if (!modified) {
			return urlPathAndQuery;
		}

		return createPath({
			pathname,
			search: urlSearchParams.toString() ? `?${urlSearchParams.toString()}` : '',
			hash
		});
	}

	/** See TS-44672 */
	private static rewriteUniformPathEncoding(urlPathAndQuery: string) {
		return urlPathAndQuery.replaceAll(
			/((?:activity\/history|architecture\/editor|delta\/[^/]+|findings\/[^/]+|metrics\/[^/]+|testgaps\/[^/]+)\/[^/]+\/)([^?]+\/[^?]+)(.*)/g,
			(_, prefix, uniformPath, rest) => prefix + encodeURIComponent(uniformPath) + rest
		);
	}

	/**
	 * Redirects old /architecture/editor/ URLs to the new /architecture/view/ or /architecture/edit/ URLs.
	 *
	 * - /architecture/editor/$projectId/$uniformPath → /architecture/view/$projectId/$uniformPath
	 * - /architecture/editor/$projectId/ (empty path) → /architecture/edit/$projectId/
	 */
	private static rewriteArchitectureEditorUrls(urlPathAndQuery: string) {
		// Match /architecture/editor/$projectId/ with optional uniform path
		const match = urlPathAndQuery.match(/\/architecture\/editor\/([^/?]+)(\/[^?]*)?(\?.*)?$/);
		if (!match) {
			return urlPathAndQuery;
		}

		const uniformPath = match[2] ?? '';

		// If there's a uniform path (not empty or just /), redirect to view
		// Otherwise redirect to edit (new architecture)
		if (uniformPath && uniformPath !== '/') {
			return urlPathAndQuery.replace(/\/architecture\/editor\//, '/architecture/view/');
		}
		return urlPathAndQuery.replace(/\/architecture\/editor\//, '/architecture/edit/');
	}
}
